import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_CLOUD_USERS_RESOURCE } from 'src/features/cloudUsers/api/useListCloudUsers';
import { GET_VALIDATOR_ACCOUNT_RESOURCE } from 'src/features/validatorAccounts/api/useGetValidatorAccount';
import { LIST_VALIDATOR_ACCOUNTS_RESOURCE } from 'src/features/validatorAccounts/api/useListValidatorAccounts';
import { useUpdateValidatorAccount } from 'src/features/validatorAccounts/api/useUpdateValidatorAccount';
import ValidatorAccountFormModal from 'src/features/validatorAccounts/components/forms/ValidatorAccountFormModal';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import { ValidatorAccount } from 'src/features/validatorAccounts/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';

interface ValidatorAccountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccount: ValidatorAccount;
    children?: React.ReactNode;
}

function UpdateValidatorAccountFormModal<TData>({
    className,
    selectedAccount,
    children,
}: ValidatorAccountFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: updateValidatorAccount, isPending } = useUpdateValidatorAccount({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VALIDATOR_ACCOUNTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_VALIDATOR_ACCOUNT_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_CLOUD_USERS_RESOURCE] });

            toast({
                title: formatMessage(validatorAccountsMessages.updateValidatorAccountSuccess),
                description: formatMessage(validatorAccountsMessages.updateValidatorAccountSuccessDescription),
            });
        },
    });

    return (
        <ValidatorAccountFormModal
            className={className}
            selectedAccount={selectedAccount}
            mutate={(values, onSuccess, onError) =>
                updateValidatorAccount({ ...values, id: selectedAccount.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(validatorAccountsMessages.editValidatorAccount)}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(validatorAccountsMessages.editValidatorAccountSubtitle, {
                accountName: selectedAccount.name,
            })}
        >
            {children}
        </ValidatorAccountFormModal>
    );
}

export default UpdateValidatorAccountFormModal;
